import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  InputSignal,
  linkedSignal,
  model, ModelSignal,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  AppService,
  Media,
  PromptDialogService,
  PresignService,
  UIConstants,
  UploadService,
  UserService,
  YoutubeVideoAd,
  PhotoOrderService,
  PhotoOrder,
} from '@lc/core';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { MatButton } from '@angular/material/button';
import { E2eIdDirective } from '@lc/ui';
import { HttpClient, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MarketingOrderService } from '@lc/ngx-ui/lib/services/marketing-order';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface PreparedFile {
  name: string;
  mimeType: AcceptedMimeTypes;
  marketingOrderId: string;
}

interface UploadState {
  isProcessing: boolean;
  uploadProgress: number;
  isSuccessful: boolean;
  errorMessage: string;
  selectedVideoFileName: string;
  videoPreviewUrl: string;
  loadingBarColor: string;
}

const enum AcceptedMimeTypes {
  MP4 = 'video/mp4',
  MOV = 'video/quicktime',
  AVI = 'video/x-msvideo',
}

const LoadingBarColors = {
  WARN: 'warn',
  PRIMARY: 'primary',
};

const MAX_FILE_SIZE_GB = 1;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_GB * 1024 * 1024 * 1024;

const ErrorMessages = {
  FORMAT_LENGTH: 'To upload a video it must be in an MP4, AVI, or MOV format and no longer than 2 minutes.',
  FILE_SIZE: `AVI video file size must not exceed ${MAX_FILE_SIZE_GB}GB.`,
  SERVER_ERROR: 'There was an error uploading the video. Please try again later.',
  DELETE_ERROR: 'There was an error deleting the video. Please try again later.',
  UPLOAD_IN_PROGRESS: 'Video upload is in progress.',
};

@Component({
  selector: 'lc-upload-video',
  templateUrl: './upload-video.component.html',
  styleUrls: ['./upload-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatIcon, NgxFileDropModule, MatButton, E2eIdDirective, MatProgressBar],
})
export class UploadVideoComponent implements OnInit {
  private readonly uploadService = inject(UploadService);
  private readonly http = inject(HttpClient);
  private readonly presignService = inject(PresignService);
  protected readonly AppService = AppService;
  private readonly marketingOrderService = inject(MarketingOrderService);
  private readonly photoOrderService = inject(PhotoOrderService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private readonly prompt = inject(PromptDialogService);
  private readonly userService = inject(UserService);
  title: InputSignal<string> = input('');
  subTitle: InputSignal<string> = input('');
  videoTips: InputSignal<string[]> = input([]);
  marketingOrderId: InputSignal<string> = input('');
  photoOrderId: InputSignal<string> = input(''); // Add photoOrderId input
  videoSubmitted: InputSignal<boolean> = input(false); // Track if video has been submitted
  video: ModelSignal<YoutubeVideoAd | null> = model(null);
  isYoutubeAd: InputSignal<boolean> = input(false);
  private marketingOrder = null;
  private photoOrder: PhotoOrder = null;
  private videoData: YoutubeVideoAd | null = null;

  // Combine all upload-related state into a single linkedSignal
  readonly uploadState: WritableSignal<UploadState> = linkedSignal({
    source: () => ({
      video: this.video(),
      photoOrderId: this.photoOrderId(),
      marketingOrderId: this.marketingOrderId(),
    }),
    computation: ({ video, photoOrderId, marketingOrderId }) => {
      // When video is set from parent (with either photoOrderId or marketingOrderId), initialize display state
      if (video && (photoOrderId || marketingOrderId)) {
        let previewUrl = '';
        if (video.uri) {
          previewUrl = video.uri;
        } else if (video.key && video.bucket) {
          previewUrl = `https://${video.bucket}.s3.us-west-2.amazonaws.com/${video.key}`;
        }

        return {
          isProcessing: true,
          uploadProgress: 100,
          isSuccessful: true,
          errorMessage: '',
          selectedVideoFileName: video.name,
          videoPreviewUrl: previewUrl,
          loadingBarColor: LoadingBarColors.PRIMARY,
        };
      }

      // When video is cleared or no order ID, reset to initial state
      return {
        isProcessing: false,
        uploadProgress: 0,
        isSuccessful: true,
        errorMessage: '',
        selectedVideoFileName: '',
        videoPreviewUrl: '',
        loadingBarColor: LoadingBarColors.PRIMARY,
      };
    },
  });

  readonly canRemoveVideo: WritableSignal<boolean> = signal(false);
  readonly videoLoadError: WritableSignal<boolean> = signal(false);

  async ngOnInit() {
    // Check if we're working with photoOrder or marketingOrder
    if (this.photoOrderId()) {
      // Load photoOrder for permissions check only
      // Parent component (manage-multimedia) handles video state initialization
      const order = await firstValueFrom(this.photoOrderService.getOrder$(this.photoOrderId()));
      this.photoOrder = order;
      this.canRemoveVideo.set(this.userService.getCurrentUser()._id === order.photoAgencyId);
    } else if (this.marketingOrderId()) {
      // Load from marketingOrder (existing behavior)
      const order = await this.marketingOrderService.getOne(this.marketingOrderId());
      this.canRemoveVideo.set(this.userService.getCurrentUser()._id === order.agentId);
      this.marketingOrder = order;
      if (order.media?.youtubeVideoAd) {
        this.videoData = order.media.youtubeVideoAd;
        // Setting video will trigger linkedSignal to recompute uploadState
        this.video.set(this.videoData);
      }
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const state = this.uploadState();
        if (state.isProcessing && state.uploadProgress < 100) {
          this.router.navigateByUrl(this.router.url, { skipLocationChange: true });
          this.prompt.openPrompt(
            'Upload in Progress',
            'Please wait until the video upload is complete.',
            UIConstants.OK,
          );
        }
      });
  }

  async handleDroppedFile(droppedFile: NgxFileDropEntry[]) {
    this.resetUploadProgress();
    this.resetUploadState();
    const fileEntry = droppedFile[0].fileEntry as FileSystemFileEntry;
    this.uploadState.update((state) => ({
      ...state,
      isProcessing: true,
      selectedVideoFileName: fileEntry.name,
    }));

    // Check video length is less than 120 seconds (2 minutes)
    const correctLength = await this.checkVideoLength(fileEntry, 120);
    if (!correctLength) {
      this.setError(ErrorMessages.FORMAT_LENGTH);
      return;
    }
    const preparedFile = await this.prepareFileForUpload(fileEntry);
    this.presignUrlAndUpload(preparedFile, fileEntry);
  }

  async removeVideo() {
    try {
      if (this.photoOrderId()) {
        // For photoOrder, use the dedicated delete method with ID directly for better performance
        await this.photoOrderService.deleteYoutubeVideoAd(this.photoOrderId());
      } else {
        // For marketingOrder, use the existing deleteFileFromBucket flow
        const orderId = this.marketingOrderId();
        await firstValueFrom(this.uploadService.deleteFileFromBucket(orderId));
        const data = new Media({
          multimedia: this.marketingOrder.media?.multimedia ?? [],
        });
        await this.marketingOrderService.updateMedia('', data);
      }
      this.resetUploadProgress();
      this.resetUploadState();
    } catch (error) {
      this.setError(ErrorMessages.DELETE_ERROR);
    }
  }

  private resetUploadProgress() {
    this.uploadState.update((state) => ({
      ...state,
      uploadProgress: 0,
      loadingBarColor: LoadingBarColors.PRIMARY,
      errorMessage: '',
    }));
  }

  private resetUploadState() {
    this.uploadState.set({
      isProcessing: false,
      uploadProgress: 0,
      isSuccessful: true,
      errorMessage: '',
      selectedVideoFileName: '',
      videoPreviewUrl: '',
      loadingBarColor: LoadingBarColors.PRIMARY,
    });
    this.video.set(null);
  }

  private setError(message: string) {
    this.uploadState.update((state) => ({
      ...state,
      loadingBarColor: LoadingBarColors.WARN,
      uploadProgress: 100,
      isSuccessful: false,
      errorMessage: message,
    }));
  }

  onVideoLoaded() {
    this.videoLoadError.set(false);
  }

  onVideoError(event: Event) {
    console.info('Video error event:', event);
    const videoElement = event.target as HTMLVideoElement;
    const error = videoElement?.error;

    // Log detailed error information
    if (error) {
      console.error('Video error code:', error.code);
      console.error('Video error message:', error.message);
    }

    // Only set error to true for actual loading errors, not CORS issues
    // CORS errors typically have error code 2 (MEDIA_ERR_NETWORK)
    if (error && error.code !== 2) {
      this.videoLoadError.set(true);
    } else {
      console.warn('Video may have CORS issue but attempting to continue');
    }
  }

  private checkVideoLength(fileEntry: FileSystemFileEntry, length: number): Promise<boolean> {
    return new Promise((resolve) => {
      // Check file extension first
      const ext = fileEntry.name.toLowerCase().split('.').pop();
      const isValidExtension = ext === 'mp4' || ext === 'mov' || ext === 'avi';

      if (!isValidExtension) {
        resolve(false);
        return;
      }

      fileEntry.file((file: File) => {
        // Check file size only for AVI files (since we can't validate their duration)
        if (ext === 'avi' && file.size > MAX_FILE_SIZE_BYTES) {
          this.setError(ErrorMessages.FILE_SIZE);
          resolve(false);
          return;
        }

        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
          window.URL.revokeObjectURL(video.src);
          const { duration } = video;
          resolve(duration <= length);
        };

        video.onerror = () => {
          window.URL.revokeObjectURL(video.src);
          // If video can't load metadata but has valid extension and size, allow it (e.g., AVI files)
          resolve(true);
        };

        video.src = URL.createObjectURL(file);
      });
    });
  }

  private prepareFileForUpload(fileEntry: FileSystemFileEntry): Promise<PreparedFile> {
    return new Promise((resolve) => {
      const preparedFile = {
        name: fileEntry.name,
        mimeType: undefined,
        marketingOrderId: this.marketingOrderId(),
      };
      fileEntry.file((f: File) => {
        preparedFile.mimeType = f.type;
        resolve(preparedFile);
      });
    });
  }

  private presignUrlAndUpload(preparedFile: PreparedFile, fileEntry: FileSystemFileEntry) {
    const orderId = this.photoOrderId() || this.marketingOrderId();
    this.uploadService
      .getPresignedUrl('YOUTUBE_VIDEO_AD_PUT', preparedFile.mimeType, `orderId=${orderId}`)
      .subscribe({
        next: async (presign: any) => {
          const file: File = await new Promise((resolve) => {
            fileEntry.file((f: File) => resolve(f));
          });

          const req = new HttpRequest('PUT', presign.url, file, {
            reportProgress: true,
          });

          this.http
            .request(req)
            .pipe(
              tap((event) => {
                if (event.type === HttpEventType.UploadProgress) {
                  const percentDone = Math.round((100 * event.loaded) / event.total);
                  this.uploadState.update((state) => ({ ...state, uploadProgress: percentDone }));
                }
              }),
              // Filter to only the HttpResponse
              filter((event) => event instanceof HttpResponse),
              map((event: HttpResponse<any>) => {
                if (event.status === 200) {
                  this.uploadState.update((state) => ({ ...state, isSuccessful: true }));
                  return event;
                }
                this.setError(ErrorMessages.SERVER_ERROR);
                throw new Error(`Upload failed with response:${event.status}`);
              }),
              catchError((err) => {
                throw err;
              }),
              switchMap(async () => {
                const videoData = {
                  key: presign.key,
                  name: fileEntry.name,
                  bucket: presign.bucket,
                };
                this.videoData = videoData;

                // Save to photoOrder or marketingOrder based on context
                if (this.photoOrderId()) {
                  // Save to photoOrder using PhotoOrderService
                  const photoOrder = await firstValueFrom(this.photoOrderService.getOrder$(this.photoOrderId()));
                  await this.photoOrderService.updateYoutubeVideoAd(photoOrder, videoData);
                  // After save, get the updated order to retrieve the URI generated by backend
                  const order = await firstValueFrom(this.photoOrderService.getOrder$(this.photoOrderId()));
                  // Setting video will trigger linkedSignal to recompute uploadState with preview URL
                  if (order.media?.youtubeVideoAd?.uri) {
                    this.video.set(order.media.youtubeVideoAd);
                  } else {
                    this.video.set(this.videoData);
                  }
                } else {
                  // Save to marketingOrder (existing behavior)
                  const data = new Media({
                    multimedia: this.marketingOrder.media?.multimedia ?? [],
                    youtubeVideoAd: videoData,
                  });
                  await this.marketingOrderService.updateMedia('', data);
                  // After save, get the updated order to retrieve the URI generated by backend
                  const order = await this.marketingOrderService.getOne(this.marketingOrderId());
                  // Setting video will trigger linkedSignal to recompute uploadState with preview URL
                  if (order.media?.youtubeVideoAd?.uri) {
                    this.video.set(order.media.youtubeVideoAd);
                  } else {
                    this.video.set(this.videoData);
                  }
                }
              }),
            )
            .subscribe({
              error: (err) => {
                console.error('Error during video upload:', err);
                // Only set error message if one hasn't been set already
                if (!this.uploadState().errorMessage) {
                  this.setError('Failed to complete video upload');
                }
              },
            });
        },
        error: (err) => {
          console.error('Error getting presigned URL:', err);
          this.setError(ErrorMessages.SERVER_ERROR);
        },
      });
  }
}
